/**
 * PGC Artwork Inquiry — direct HubSpot Forms API v3 submission.
 * Ersetzt das gerenderte HubSpot-Formular im Inquiry-Modal.
 * Alle artwork_*-Felder werden EXPLIZIT übergeben — kein Hidden-Field-Renderer mehr.
 * 2026-09-27: Telefonfeld (Pflicht) -> HubSpot-Property 'mobilephone' (Mobile phone number; so im Formular als Pflichtfeld angelegt);
 *             Datenschutz-Einwilligung via legalConsentOptions.
 * 2026-10-12: Anrede (Pflicht) -> HubSpot-Property 'salutation';
 *             Einwilligungen wie im HubSpot-Formular (explicit_consent_to_process):
 *             Pflicht-Haken "store and process" -> consentToProcess,
 *             freiwilliger Haken "other communications" -> Abo 10354461.
 * 2026-10-12 (v15): nur noch EIN freiwilliger Marketing-Haken (Abo 10354461, nur gesendet wenn angehakt)
 *             + Datenschutz-Hinweis als consentToProcess-Text; kein Pflicht-Haken mehr.
 *             Mit Haken zusätzlich 'website_newsletter_optin' = requested (-> Make -> Mailchimp).
 */
const PGC_HS_PORTAL = '7949537';
const PGC_HS_FORM = '2089c9fc-277c-4396-838e-eaf7a9067baa';
const PGC_HS_SUBSCRIPTION_ID = 10354461; // Marketing-Abo des HubSpot-Formulars
const PGC_HS_ENDPOINT =
  `https://api.hsforms.com/submissions/v3/integration/submit/${PGC_HS_PORTAL}/${PGC_HS_FORM}`;

function pgcGetCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : undefined;
}

/**
 * @param {Object} d
 * @param {string} d.salutation    Pflicht (HubSpot-Property 'salutation')
 * @param {string} d.email          Pflicht
 * @param {string} d.firstname     Pflicht
 * @param {string} d.lastname      Pflicht
 * @param {string} d.mobilephone   Pflicht (HubSpot-Property 'mobilephone')
 * @param {string} d.message       optional
 * @param {string} d.privacy_text  Wortlaut des Datenschutz-Hinweises (consentToProcess-Text)
 * @param {boolean} d.marketing_optin  freiwilliger Marketing-Haken (nur true wird gesendet)
 * @param {string} d.marketing_text    Wortlaut dieses Hakens
 * @param {string} d.artwork_artist  Pflicht — ohne dieses Feld entsteht in
 *                                    HubSpot KEIN Deal (Make-Filter).
 * @param {string} d.artwork_title   Pflicht
 * @param {string} d.artwork_variant optional (Grösse/Edition)
 * @param {string} d.artwork_url     optional; Default = aktuelle Produkt-URL
 * @param {string} d.pricelist_link  optional; verstecktes Feld "Artwork Pricelist Link" (HubSpot 'website')
 * @returns {Promise<Object>} HubSpot-Antwort (inlineMessage bei Erfolg)
 */
async function pgcSubmitInquiry(d) {
  // Guard AN DER QUELLE — nie stumm ohne Artist abschicken:
  const required = ['salutation', 'email', 'firstname', 'lastname', 'mobilephone', 'artwork_artist', 'artwork_title'];
  const missing = required.filter((k) => !d[k] || !String(d[k]).trim());
  if (missing.length) {
    throw new Error('PGC inquiry blocked — missing fields: ' + missing.join(', '));
  }

  const field = (name, value) => ({ objectTypeId: '0-1', name, value: String(value) });
  const fields = [
    field('salutation', d.salutation),
    field('email', d.email),
    field('firstname', d.firstname),
    field('lastname', d.lastname),
    field('mobilephone', String(d.mobilephone).trim()),
    field('artwork_artist', d.artwork_artist),
    field('artwork_title', d.artwork_title),
  ];
  if (d.message) fields.push(field('message', d.message));
  if (d.artwork_variant) fields.push(field('artwork_variant', d.artwork_variant));
  fields.push(field('artwork_url', d.artwork_url || window.location.href));
  // Newsletter-Haken -> HubSpot-Markierung; Make trägt den Kontakt in Mailchimp ein (Double-Opt-in). Ohne Haken nie gesendet.
  if (d.marketing_optin) fields.push(field('website_newsletter_optin', 'requested'));
  // Verstecktes Feld "Artwork Pricelist Link" (HubSpot-Formularfeld 'website' = Firmen-Property) — nur senden, wenn gesetzt:
  if (d.pricelist_link && String(d.pricelist_link).trim()) {
    fields.push({ objectTypeId: '0-2', name: 'website', value: String(d.pricelist_link).trim() });
  }

  const payload = {
    submittedAt: Date.now(),
    fields,
    context: {
      hutk: pgcGetCookie('hubspotutk'), // Analytics-Attribution, darf fehlen
      pageUri: window.location.href,
      pageName: document.title,
    },
    // Datenschutz (seit v15): kein Verarbeitungs-Haken mehr. Der Hinweistext gilt mit dem Absenden
    // (HubSpot "implicit consent to process"). Marketing-Abo NUR bei aktivem Haken mitsenden;
    // ohne Haken wird der Abo-Status in HubSpot nicht angefasst (kein Abmelden, kein Anmelden).
    legalConsentOptions: {
      consent: Object.assign(
        {
          consentToProcess: true,
          text: d.privacy_text || 'We use your personal information to respond to your enquiry and provide our services. Your data is stored and processed in accordance with our Privacy Policy.',
        },
        d.marketing_optin
          ? {
              communications: [
                {
                  value: true,
                  subscriptionTypeId: PGC_HS_SUBSCRIPTION_ID,
                  text: d.marketing_text || 'I would like to receive news, exhibition invitations and updates from Petra Gut Contemporary AG.',
                },
              ],
            }
          : {}
      ),
    },
  };

  const res = await fetch(PGC_HS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let detail = '';
    try { detail = JSON.stringify(await res.json()); } catch (_) {}
    throw new Error(`HubSpot submission failed (${res.status}): ${detail}`);
  }
  return res.json();
}

// Export für Theme-Bundler ODER global für inline-Snippets:
window.pgcSubmitInquiry = pgcSubmitInquiry;
